import React, { useMemo, useEffect, useState } from 'react';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import {
  useVariationsAnalyticStore,
  GroupByFieldType,
  mapVariationsFilterToRequestType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/variationsAnalyticStore';
import { BarChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { VariationsByCategoryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsByCategoryAdapter';
import { SeriesColumnOptions } from 'highcharts';

interface QueryFnMap {
  [requestType: string]: {
    [groupById: string]: () => Promise<VariationsByCategoryAdapter>;
  };
}

interface VariationsByCategoryBarChartProps {
  metrics: Array<{ id: string; label: string }>;
  groupByFields: Array<{ id: string; label: string }>;
  queryFnMap: QueryFnMap;
  renderSubtitle: (selectedMetric: string, selectedGroupBy: string, data?: BarChartData) => string | React.ReactNode;
}

export const VariationsByCategoryBarChart = (props: VariationsByCategoryBarChartProps) => {
  const { metrics, groupByFields, queryFnMap, renderSubtitle } = props;
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { variationsFilter } = useVariationsAnalyticStore();
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByFieldType>(GroupByFieldType.Category);

  const currentRequestType = mapVariationsFilterToRequestType(variationsFilter);
  const currentGroupById = selectedGroupBy;

  const currentQueryFn = useMemo(() => {
    return queryFnMap[currentRequestType]?.[currentGroupById] || (() => Promise.resolve([]));
  }, [queryFnMap, currentRequestType, currentGroupById]);

  const getCurrentMetricLabel = () => {
    return metrics.find((metric) => metric.id === variationsFilter)?.label || '';
  };

  const handleGroupByChange = (option: { id: string }) => {
    setSelectedGroupBy(option.id as GroupByFieldType);
  };

  useEffect(() => {
    setSelectedGroupBy(groupByFields[0].id as GroupByFieldType);
  }, [groupByFields]);

  return (
    <BeaconProChart
      name={`variations-double-dropdown-bar-chart-${currentRequestType}-${currentGroupById}`}
      queryFn={currentQueryFn}
    >
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">{getCurrentMetricLabel()}</Text>
          <Text variant="h4">by</Text>
          {groupByFields.length > 1 ? (
            <SlDropdownMenu
              options={groupByFields.map((groupBy) => ({
                id: groupBy.id,
                label: groupBy.label
              }))}
              buttonVariant="title"
              defaultLabel=""
              onChange={handleGroupByChange}
              selectedId={currentGroupById}
            />
          ) : (
            <Text variant="h4">{groupByFields[0]?.label || ''}</Text>
          )}
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(adapter: ChartDataAdapter<BarChartData>) => {
          return renderSubtitle(variationsFilter, currentGroupById, adapter.transform());
        }}
      />
      <BeaconProChart.Legend
        primaryLegendProps={{ displayName: mainTimePeriod.displayName }}
        comparisonLegendProps={{ displayName: comparisonTimePeriod.displayName }}
      />
      <BeaconProChart.Visualization
        render={(data: VariationsByCategoryAdapter) => {
          const { chartData } = data.transform();
          return <TopEntityChartV2 chartSeries={chartData as SeriesColumnOptions[]} />;
        }}
      />
    </BeaconProChart>
  );
};
